import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './Project.css';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import transition from '../../transition';
import { projects } from '../../Data/projectsData';
import TechStackModal from '../TechStack/tech-stack-modal';
import { techStacks } from '../../Data/techStackData';
import ImageViewer from 'react-simple-image-viewer';

// Throttle function
const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Add an image optimization hook to handle image loading and processing
const useOptimizedImage = (src) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) return;

    const img = new Image();
    img.onload = () => setLoaded(true);
    img.onerror = () => setError(true);
    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return { loaded, error };
};

// Create an optimized image component with performance enhancements
const OptimizedImage = React.memo(({ src, alt, onClick, className, style }) => {
  const { loaded, error } = useOptimizedImage(src);
  const imgRef = useRef(null);

  useEffect(() => {
    if (imgRef.current && 'loading' in HTMLImageElement.prototype) {
      imgRef.current.loading = 'lazy';
    }

    if (imgRef.current && 'decoding' in HTMLImageElement.prototype) {
      imgRef.current.decoding = 'async';
    }
  }, []);

  return (
    <div
      className={className}
      style={{
        ...style,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'rgba(0,0,0,0.05)',
      }}
      onClick={onClick}
    >
      {!loaded && !error && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.05)',
          }}
        >
          <div className="spinner" style={{ width: '30px', height: '30px' }}></div>
        </div>
      )}

      {error && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.05)',
            color: '#888',
          }}
        >
          Failed to load image
        </div>
      )}

      <img
        ref={imgRef}
        src={src}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.5s ease',
        }}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
});

const Project = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const { projectSlug } = useParams();
  const project = projects.find((p) => p.link === `/project/${projectSlug}`);
  const isTransitioning = useRef(false);
  const lastScrollTime = useRef(0);
  const titleRefs = useRef([]);
  const sectionRefs = useRef([]);
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [showBanner, setShowBanner] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showTechStackModal, setShowTechStackModal] = useState(false);
  const [currentTechStack, setCurrentTechStack] = useState(null);

  // Image viewer states
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [imageUrls, setImageUrls] = useState([]);
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Helper function to determine if the intro media is a video or image
  const isVideo = useRef(
    project &&
      project.intro &&
      project.intro.video &&
      (typeof project.intro.video === 'string'
        ? project.intro.video.endsWith('.mp4') || project.intro.video.endsWith('.mov')
        : project.intro.video.includes &&
          (project.intro.video.includes('.mp4') || project.intro.video.includes('.mov')))
  ).current;

  // Helper function to determine if the project is a web application
  const isWebProject = useRef(project && project.type && project.type.toLowerCase().includes('web')).current;

  // Handle window resize and update isMobile state
  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth <= 768;
      if (isMobileView !== isMobile) {
        setIsMobile(isMobileView);
        console.log('Layout changed to:', isMobileView ? 'mobile' : 'desktop');
      }
    };

    // Use requestAnimationFrame for smoother transitions during viewport manipulation
    const throttledResize = () => {
      window.requestAnimationFrame(handleResize);
    };

    window.addEventListener('resize', throttledResize);
    return () => window.removeEventListener('resize', throttledResize);
  }, [isMobile]);

  // Reset refs when switching between mobile and desktop
  useEffect(() => {
    // Reset the refs when layout changes
    sectionRefs.current = [];
    titleRefs.current = [];

    // Reset current slide when switching to desktop mode
    if (!isMobile) {
      setCurrentSlide(0);
    }
  }, [isMobile]);

  // Optimize slide transitions to reduce jank
  const slideVariants = useMemo(
    () => ({
      enter: (direction) => ({
        opacity: 0,
        x: direction > 0 ? 100 : -100,
        transition: {
          duration: 0.35,
          ease: [0.4, 0.0, 0.2, 1],
        },
      }),
      center: {
        opacity: 1,
        x: 0,
        transition: {
          duration: 0.5,
          ease: [0.4, 0.0, 0.2, 1],
        },
      },
      exit: (direction) => ({
        opacity: 0,
        x: direction < 0 ? 100 : -100,
        transition: {
          duration: 0.35,
          ease: [0.4, 0.0, 0.2, 1],
        },
      }),
    }),
    []
  );

  const slideTransition = useMemo(
    () => ({
      duration: 0.5,
      ease: [0.4, 0.0, 0.2, 1],
    }),
    []
  );

  // Optimize change slide function to be more performant
  const changeSlide = useCallback(
    (newDirection) => {
      if (isTransitioning.current) {
        return;
      }

      const featuresContainer = document.querySelector('.features-list-container');
      if (currentSlide === 4 && featuresContainer) {
        const isAtTop = featuresContainer.scrollTop === 0;
        if (!(isAtTop && newDirection < 0)) {
          return;
        }
      }

      const now = Date.now();
      if (now - lastScrollTime.current < 500) {
        // Increased from 400ms to 500ms for smoother transitions
        return;
      }

      if (newDirection > 0 && currentSlide < 4) {
        setDirection(1);
        setCurrentSlide((prev) => prev + 1);
        isTransitioning.current = true;
        lastScrollTime.current = now;
      } else if (newDirection < 0 && currentSlide > 0) {
        setDirection(-1);
        setCurrentSlide((prev) => prev - 1);
        isTransitioning.current = true;
        lastScrollTime.current = now;
      }

      // Use requestAnimationFrame for smoother timing
      requestAnimationFrame(() => {
        setTimeout(() => {
          isTransitioning.current = false;
        }, 650); // Increased from 500ms to 650ms to match longer animation durations
      });
    },
    [currentSlide]
  );

  // Optimize wheel event handler
  useEffect(() => {
    const handleWheel = (e) => {
      // Prevent wheel event handling when tech stack modal is open
      if (showTechStackModal) {
        return;
      }

      e.preventDefault();
      changeSlide(e.deltaY);
    };

    const throttledWheel = throttle(handleWheel, 650); // Increased from 500ms to 650ms for smoother scrolling

    window.addEventListener('wheel', throttledWheel, { passive: false });
    return () => window.removeEventListener('wheel', throttledWheel);
  }, [changeSlide, showTechStackModal]);

  // Modified image variants with simpler animations and improved performance
  const imageVariants = useMemo(
    () => ({
      hidden: {
        opacity: 0,
        scale: 1.05,
        transition: {
          duration: 0.13,
          ease: [0.4, 0, 0.2, 1],
        },
      },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          duration: 1.0,
          ease: [0.4, 0, 0.2, 1],
        },
      },
    }),
    []
  );

  const imageScaleVariants = useMemo(
    () => ({
      hidden: {
        scale: 1.05,
        opacity: 0,
        transition: {
          duration: 0.13,
          ease: [0.4, 0, 0.2, 1],
        },
      },
      visible: {
        scale: 1,
        opacity: 1,
        transition: {
          duration: 1.0,
          ease: [0.4, 0, 0.2, 1],
        },
      },
    }),
    []
  );

  // Simplified content variants for better performance
  const contentVariants = useMemo(
    () => ({
      hidden: {
        opacity: 0,
        y: 20,
        transition: {
          duration: 0.38,
          ease: [0.4, 0, 0.2, 1],
        },
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.65,
          ease: [0.4, 0, 0.2, 1],
        },
      },
    }),
    []
  );

  const itemVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: 10 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.5,
          ease: [0.4, 0, 0.2, 1],
        },
      },
    }),
    []
  );

  const [currentSection, setCurrentSection] = useState('');

  useEffect(() => {
    if (isMobile) {
      const observerOptions = {
        root: null,
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0,
      };

      const observerCallback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrentSection(entry.target.dataset.section);
          }
        });
      };

      const observer = new IntersectionObserver(observerCallback, observerOptions);

      // Clean up references that might be null
      sectionRefs.current = sectionRefs.current.filter((ref) => ref !== null);

      // Force a small delay to ensure DOM has fully updated after viewport changes
      const timer = setTimeout(() => {
        // Re-query for all section elements directly
        const sectionElements = document.querySelectorAll('[data-section]');

        // Update our refs and observe each element
        sectionElements.forEach((el) => {
          observer.observe(el);
        });

        console.log('Observing sections:', sectionElements.length);
      }, 100);

      return () => {
        clearTimeout(timer);
        if (observer) {
          // Disconnect before cleaning up to prevent errors
          observer.disconnect();
        }
      };
    }
  }, [isMobile]);

  // Add this useEffect to handle video progress
  useEffect(() => {
    const video = videoRef.current;
    if (video && isVideo) {
      const updateProgress = () => {
        setProgress((video.currentTime / video.duration) * 100);
      };
      video.addEventListener('timeupdate', updateProgress);
      return () => video.removeEventListener('timeupdate', updateProgress);
    }
  }, [currentSlide, isVideo]);

  // Modify the togglePlayPause function
  const togglePlayPause = useCallback(() => {
    if (videoRef.current && isVideo) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying, isVideo, videoRef]);

  // Add this useEffect to handle video state when changing slides
  useEffect(() => {
    if (videoRef.current && isVideo) {
      if (currentSlide === 0) {
        videoRef.current.play();
        setIsPlaying(true);
        // Show the banner briefly when the video starts
        setShowBanner(true);
        setTimeout(() => setShowBanner(false), 5000); // Hide after 5 seconds
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
        setShowBanner(false);
      }
    }
  }, [currentSlide, isVideo]);

  // Add this useEffect to handle features section scrolling
  useEffect(() => {
    if (currentSlide === 4) {
      const featuresContainer = document.querySelector('.features-list-container');
      if (featuresContainer) {
        const handleFeaturesScroll = (e) => {
          if (featuresContainer.scrollTop > 0) {
            e.stopPropagation();
          }
        };

        featuresContainer.addEventListener('wheel', handleFeaturesScroll, { passive: false });
        return () => featuresContainer.removeEventListener('wheel', handleFeaturesScroll);
      }
    }
  }, [currentSlide]);

  // Function to open tech stack modal with the appropriate tech stack
  const openTechStackModal = useCallback(() => {
    // Determine which tech stack to show based on project type
    let techStackToShow = null;

    if (project && project.type) {
      if (project.type.toLowerCase().includes('mobile')) {
        techStackToShow = techStacks.mobileApp;
        console.log('Selected mobile tech stack');
      } else if (project.type.toLowerCase().includes('web')) {
        techStackToShow = techStacks.webApp;
        console.log('Selected web tech stack');
      }
      // Add conditions for other project types as needed
    }

    if (techStackToShow) {
      setCurrentTechStack(techStackToShow);
      setShowTechStackModal(true);
      console.log('Opening tech stack modal with:', techStackToShow);
    } else {
      console.warn('No tech stack found for project type:', project?.type);
    }
  }, [project, setCurrentTechStack, setShowTechStackModal]);

  // Function to close tech stack modal
  const closeTechStackModal = () => {
    setShowTechStackModal(false);
  };

  // Enhanced image viewer function with loading state and optimization for features slide
  const openImageViewer = useCallback(
    (index, images) => {
      setIsImageLoading(true);
      // Extract all image URLs if provided, or use project features
      const urls = images || project.features.map((feature) => feature.image);

      // Set URLs first so ImageViewer can initialize even before image loads
      setImageUrls(urls);
      setCurrentImage(index);

      // Use a lightweight image loading approach in a separate process
      requestAnimationFrame(() => {
        const img = new Image();
        img.onload = () => {
          setTimeout(() => {
            setIsViewerOpen(true);
            setTimeout(() => {
              setIsImageLoading(false);
            }, 300); // Small delay for smoother transition
          }, 100); // Small delay to ensure UI is ready
        };
        img.onerror = () => {
          console.error('Failed to load image');
          setIsImageLoading(false);
          // Show a notification or fallback if needed
          alert('Failed to load the image. Please try again.');
        };
        img.src = urls[index];
      });
    },
    [project]
  );

  // Also modify the image preloading strategy for better performance
  useEffect(() => {
    if (project && project.features && project.features.length > 0) {
      // Only preload images that might be visible soon
      const preloadInitialImages = () => {
        // Preload only the first few images to improve initial loading performance
        const imagesToPreload = project.features.slice(0, 2);
        imagesToPreload.forEach((feature) => {
          const img = new Image();
          img.src = feature.image;
        });
      };

      // Use requestIdleCallback or setTimeout for non-critical preloading
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preloadInitialImages);
      } else {
        setTimeout(preloadInitialImages, 1000);
      }

      // Add intersection observer to preload images as user scrolls
      if ('IntersectionObserver' in window && currentSlide === 4) {
        const featureElements = document.querySelectorAll('.feature-list-item');

        const imageObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const featureIndex = parseInt(entry.target.id.split('-')[1], 10);
                if (project.features[featureIndex] && project.features[featureIndex].image) {
                  const img = new Image();
                  img.src = project.features[featureIndex].image;
                  // Once loaded, stop observing this element
                  observer.unobserve(entry.target);
                }
              }
            });
          },
          {
            rootMargin: '250px 0px', // Increased from 200px to 250px to preload earlier
            threshold: 0.05, // Reduced threshold to start loading sooner
          }
        );

        featureElements.forEach((el) => {
          imageObserver.observe(el);
        });

        return () => {
          if (imageObserver) {
            featureElements.forEach((el) => {
              imageObserver.unobserve(el);
            });
          }
        };
      }
    }
  }, [project, currentSlide]);

  // Function to close the image viewer
  const closeImageViewer = () => {
    setIsViewerOpen(false);
  };

  // Add keyboard navigation for image viewer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isViewerOpen) return;

      switch (e.key) {
        case 'Escape':
          closeImageViewer();
          break;
        case 'ArrowRight':
          if (currentImage < imageUrls.length - 1) {
            setCurrentImage((prevIndex) => prevIndex + 1);
          }
          break;
        case 'ArrowLeft':
          if (currentImage > 0) {
            setCurrentImage((prevIndex) => prevIndex - 1);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isViewerOpen, currentImage, imageUrls.length]);

  // Add an image loading overlay component
  const ImageLoadingOverlay = () => (
    <motion.div
      className="image-loading-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="spinner"></div>
      <p>Loading image...</p>
    </motion.div>
  );

  // Wrap renderMobileContent in useCallback to prevent unnecessary re-renders
  const renderMobileContent = useCallback(
    () => (
      <div className="mobile-content">
        <motion.div
          className={`sticky-header visible`}
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Link to="/">
            <motion.div
              className="back-button"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
            >
              <div className="back">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M19 12H5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 19L5 12L12 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </motion.div>
          </Link>
          <AnimatePresence mode="sync">
            <motion.h2
              key={currentSection || 'Intro'}
              initial={{ opacity: 0, y: -50, x: 0 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              exit={{ opacity: 0, y: -50, x: 0 }}
              transition={{
                type: 'tween',
                ease: 'easeInOut',
                duration: 0.5,
              }}
              style={{ position: 'absolute', right: 16 }}
            >
              {currentSection || 'Intro'}
            </motion.h2>
          </AnimatePresence>
        </motion.div>
        <div className="project-section project-info" ref={(el) => (sectionRefs.current[0] = el)} data-section="Intro">
          <motion.div className="project-text-content" variants={contentVariants} initial="hidden" animate="visible">
            <motion.h1 ref={(el) => (titleRefs.current[0] = el)} variants={itemVariants}>
              {project.name}
            </motion.h1>

            {/* Tech Stack Button - Repositioned */}
            <motion.button
              className="tech-stack-button"
              onClick={openTechStackModal}
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
                transition: {
                  duration: 0.5,
                  delay: 0.5,
                  ease: [0.4, 0, 0.2, 1],
                },
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Tech Stack
            </motion.button>

            <motion.p variants={itemVariants}>{project.intro.text}</motion.p>
          </motion.div>
          {isWebProject ? (
            <motion.div
              className="web-container"
              variants={imageVariants}
              initial="hidden"
              animate="visible"
              onClick={() => openImageViewer(0, [project.intro.video])}
              style={{ cursor: 'pointer' }}
            >
              <motion.img
                src={project.intro.video}
                alt={project.name}
                variants={imageScaleVariants}
                initial="hidden"
                animate="visible"
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  borderRadius: '8px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                }}
              />
            </motion.div>
          ) : (
            <div className="smartphone-container">
              <motion.div className="smartphone" variants={imageVariants} initial="hidden" animate="visible">
                <div className="content">
                  {isVideo ? (
                    <>
                      <video
                        ref={videoRef}
                        src={project.intro.video}
                        autoPlay
                        muted
                        loop
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <div className="video-progress-bar">
                        <div className="video-progress" style={{ width: `${progress}%` }}></div>
                      </div>
                      <AnimatePresence>
                        {showBanner && (
                          <motion.div
                            className="funny-banner"
                            initial={{ y: 50, opacity: 0 }}
                            animate={{
                              y: 0,
                              opacity: 1,
                              transition: {
                                y: { type: 'spring', stiffness: 300, damping: 30 },
                                opacity: { duration: 2 },
                              },
                            }}
                            exit={{ y: 50, opacity: 0 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <p>Warning: Video edited by a code enthusiast, not a Hollywood pro! 🎬🤓</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <motion.img
                      src={project.intro.video}
                      alt={project.name}
                      variants={imageScaleVariants}
                      initial="hidden"
                      animate="visible"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onClick={() => openImageViewer(0, [project.intro.video])}
                    />
                  )}
                </div>
                {isVideo && (
                  <button className="home-button" onClick={togglePlayPause}>
                    {isPlaying ? '❚❚' : '▶'}
                  </button>
                )}
              </motion.div>
            </div>
          )}
        </div>
        <div
          className="project-section project-challenge"
          ref={(el) => (sectionRefs.current[1] = el)}
          data-section="Challenge"
        >
          <motion.div className="project-text-content" variants={contentVariants} initial="hidden" animate="visible">
            <motion.h2 ref={(el) => (titleRefs.current[1] = el)} variants={itemVariants}>
              Challenge
            </motion.h2>
            <motion.p variants={itemVariants}>{project.challenge.text}</motion.p>
          </motion.div>
        </div>
        <div
          className="project-section project-process"
          ref={(el) => (sectionRefs.current[2] = el)}
          data-section="Process"
        >
          <motion.div className="project-text-content" variants={contentVariants} initial="hidden" animate="visible">
            <motion.h2 ref={(el) => (titleRefs.current[2] = el)} variants={itemVariants}>
              Process
            </motion.h2>
            <motion.p variants={itemVariants}>{project.process.text}</motion.p>
          </motion.div>
        </div>
        <div
          className="project-section project-solution"
          ref={(el) => (sectionRefs.current[3] = el)}
          data-section="Solution"
        >
          <motion.div className="project-text-content" variants={contentVariants} initial="hidden" animate="visible">
            <motion.h2 ref={(el) => (titleRefs.current[3] = el)} variants={itemVariants}>
              Solution
            </motion.h2>
            <motion.p variants={itemVariants}>{project.solution.text}</motion.p>
          </motion.div>
        </div>
        <div
          className="project-section project-features"
          ref={(el) => (sectionRefs.current[4] = el)}
          data-section="Features"
        >
          <motion.h2 ref={(el) => (titleRefs.current[4] = el)} variants={itemVariants}>
            Features
          </motion.h2>
          <div className="features-grid">
            {project.features.map((feature, index) => (
              <motion.div
                key={index}
                className={`feature ${isWebProject ? 'web-feature-mobile' : ''}`}
                variants={itemVariants}
              >
                <motion.h3 variants={itemVariants}>{feature.title}</motion.h3>
                <motion.p variants={itemVariants}>{feature.description}</motion.p>
                <OptimizedImage
                  src={feature.image}
                  alt={feature.title}
                  className="feature-image"
                  onClick={() => openImageViewer(index)}
                  style={{ cursor: 'pointer', width: '100%', height: '100%' }}
                />
              </motion.div>
            ))}
          </div>
        </div>
        <div className="mobile-bottom-spacing"></div>
      </div>
    ),
    [
      currentSection,
      project,
      isWebProject,
      isVideo,
      openImageViewer,
      openTechStackModal,
      progress,
      showBanner,
      videoRef,
      isPlaying,
      togglePlayPause,
      contentVariants,
      imageScaleVariants,
      imageVariants,
      itemVariants,
    ]
  );

  // Optimized features rendering with reduced animation complexity
  const renderFeaturesSlide = useCallback(() => {
    return (
      <motion.div
        className="project-features section-container"
        variants={contentVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2 variants={itemVariants} className="features-title">
          Features
        </motion.h2>
        <div className="features-list-container">
          {project.features.map((feature, index) => (
            <motion.div
              key={index}
              id={`feature-${index}`}
              className={`feature-list-item ${isWebProject ? 'web-feature' : ''}`}
              variants={{
                hidden: {
                  opacity: 0,
                  y: 30,
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.65,
                    ease: [0.4, 0, 0.2, 1],
                    delay: Math.min(0.1 * index, 0.6), // Cap maximum delay
                  },
                },
              }}
            >
              {isWebProject ? (
                <motion.div className="web-feature-content">
                  <div className="feature-text-web">
                    <motion.h3>{feature.title}</motion.h3>
                    <motion.p>{feature.description}</motion.p>
                  </div>
                  <motion.div
                    className="feature-img-web"
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.65,
                          ease: [0.4, 0, 0.2, 1],
                          delay: Math.min(0.25 + 0.06 * index, 0.6), // Cap maximum delay
                        },
                      },
                    }}
                    onClick={() => openImageViewer(index)}
                    style={{ cursor: 'pointer' }}
                  >
                    <OptimizedImage
                      src={feature.image}
                      alt={feature.title}
                      className="feature-image"
                      style={{ cursor: 'pointer', width: '100%', height: '100%' }}
                    />
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div className="feature-content">
                  <div className="feature-text">
                    <motion.h3>{feature.title}</motion.h3>
                    <motion.p>{feature.description}</motion.p>
                  </div>
                  <motion.div
                    className="feature-img-mobile"
                    variants={{
                      hidden: {
                        opacity: 0,
                        scale: 1.05,
                      },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 0.65,
                          ease: [0.4, 0, 0.2, 1],
                          delay: Math.min(0.25 + 0.06 * index, 0.6), // Cap maximum delay
                        },
                      },
                    }}
                    onClick={() => openImageViewer(index)}
                    style={{ cursor: 'pointer' }}
                  >
                    <OptimizedImage
                      src={feature.image}
                      alt={feature.title}
                      className="feature-image"
                      style={{ cursor: 'pointer', width: '100%', height: '100%' }}
                    />
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
    );
  }, [project, isWebProject, openImageViewer, contentVariants, itemVariants]);

  // Create an optimized wrapper for the slide content
  const SlideContent = useMemo(() => {
    // Pre-render all slides to prevent lag during transitions
    const slides = {
      0: (
        <div className="project-info section-container">
          {/* Project info slide content */}
          <motion.div className="project-text-content" variants={contentVariants} initial="hidden" animate="visible">
            <motion.h1 variants={itemVariants}>{project.name}</motion.h1>
            <motion.button
              className="tech-stack-button"
              onClick={openTechStackModal}
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
                transition: {
                  duration: 0.5,
                  delay: 0.5,
                  ease: [0.4, 0, 0.2, 1],
                },
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Tech Stack
            </motion.button>
            <motion.p variants={itemVariants}>{project.intro.text}</motion.p>
          </motion.div>
          {/* Content for project intro slide */}
          {isWebProject ? (
            <motion.div
              className="web-container"
              variants={imageVariants}
              initial="hidden"
              animate="visible"
              onClick={() => openImageViewer(0, [project.intro.video])}
              style={{ cursor: 'pointer' }}
            >
              <motion.img
                src={project.intro.video}
                alt={project.name}
                variants={imageScaleVariants}
                initial="hidden"
                animate="visible"
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  borderRadius: '8px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                }}
              />
            </motion.div>
          ) : (
            <div className="smartphone-container">
              <motion.div className="smartphone" variants={imageVariants} initial="hidden" animate="visible">
                <div className="content">
                  {isVideo ? (
                    <>
                      <video
                        ref={videoRef}
                        src={project.intro.video}
                        autoPlay
                        muted
                        loop
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <div className="video-progress-bar">
                        <div className="video-progress" style={{ width: `${progress}%` }}></div>
                      </div>
                      <AnimatePresence>
                        {showBanner && (
                          <motion.div
                            className="funny-banner"
                            initial={{ y: 50, opacity: 0 }}
                            animate={{
                              y: 0,
                              opacity: 1,
                              transition: {
                                y: { type: 'spring', stiffness: 300, damping: 30 },
                                opacity: { duration: 2 },
                              },
                            }}
                            exit={{ y: 50, opacity: 0 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <p>Warning: Video edited by a code enthusiast, not a Hollywood pro! 🎬🤓</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <motion.img
                      src={project.intro.video}
                      alt={project.name}
                      variants={imageScaleVariants}
                      initial="hidden"
                      animate="visible"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onClick={() => openImageViewer(0, [project.intro.video])}
                    />
                  )}
                </div>
                {isVideo && (
                  <button className="home-button" onClick={togglePlayPause}>
                    {isPlaying ? '❚❚' : '▶'}
                  </button>
                )}
              </motion.div>
            </div>
          )}
        </div>
      ),
      1: (
        <motion.div
          className="project-challenge section-container"
          variants={contentVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="project-text-content">
            <motion.h2 variants={itemVariants}>Challenge</motion.h2>
            <motion.p variants={itemVariants}>{project.challenge.text}</motion.p>
          </motion.div>
        </motion.div>
      ),
      2: (
        <motion.div
          className="project-process section-container"
          variants={contentVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="project-text-content">
            <motion.h2 variants={itemVariants}>Process</motion.h2>
            <motion.p variants={itemVariants}>{project.process.text}</motion.p>
          </motion.div>
        </motion.div>
      ),
      3: (
        <motion.div
          className="project-solution section-container"
          variants={contentVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="project-text-content">
            <motion.h2 variants={itemVariants}>Solution</motion.h2>
            <motion.p variants={itemVariants}>{project.solution.text}</motion.p>
          </motion.div>
        </motion.div>
      ),
      4: renderFeaturesSlide(),
    };

    return slides[currentSlide] || null;
  }, [
    currentSlide,
    project,
    isWebProject,
    renderFeaturesSlide,
    openImageViewer,
    openTechStackModal,
    contentVariants,
    itemVariants,
    imageScaleVariants,
    isPlaying,
    isVideo,
    progress,
    showBanner,
    imageVariants,
    togglePlayPause,
  ]);

  // Replace renderSlide function with the optimized version
  const renderSlide = useCallback(() => {
    if (isMobile) {
      return renderMobileContent();
    } else {
      return SlideContent;
    }
  }, [isMobile, SlideContent, renderMobileContent]);

  console.log('Rendering slide:', currentSlide);

  return (
    <>
      <div className="bg"></div>
      {!isMobile && (
        <Link to="/">
          <motion.div
            className="back-button"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
          >
            <div className="back">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M12 19L5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </motion.div>
        </Link>
      )}
      <div
        className={`project-container ${isMobile ? 'mobile' : ''}`}
        key={`layout-${isMobile ? 'mobile' : 'desktop'}`}
      >
        {isMobile ? (
          renderMobileContent()
        ) : (
          <AnimatePresence initial={true} custom={direction} mode="wait">
            <motion.div
              key={currentSlide}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="slide"
            >
              {renderSlide()}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
      {!isMobile && (
        <motion.div
          className="navigation-dots"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
        >
          {[0, 1, 2, 3, 4].map((index) => (
            <motion.div
              key={index}
              className={`dot ${currentSlide === index ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          ))}
        </motion.div>
      )}

      {/* Tech Stack Modal */}
      <TechStackModal isOpen={showTechStackModal} onClose={closeTechStackModal} techStack={currentTechStack} />

      {/* Image Loading Overlay */}
      <AnimatePresence>{isImageLoading && <ImageLoadingOverlay />}</AnimatePresence>

      {/* Image Viewer with improved error handling */}
      {isViewerOpen && (
        <ImageViewer
          src={imageUrls}
          currentIndex={currentImage}
          onClose={closeImageViewer}
          disableScroll={true}
          backgroundStyle={{
            backgroundColor: 'rgba(0,0,0,0.9)',
            zIndex: 9999,
          }}
          leftArrowComponent={
            <div
              style={{
                fontSize: '40px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                borderRadius: '10%',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ❮
            </div>
          }
          rightArrowComponent={
            <div
              style={{
                fontSize: '40px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                padding: '10px',
                borderRadius: '10%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ❯
            </div>
          }
          closeOnClickOutside={true}
          closeComponent={
            <div
              style={{
                fontSize: '40px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: 'rgba(0,0,0)',
                padding: '10px',
                borderRadius: '10%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </div>
          }
          onError={(e, index) => {
            console.error(`Error loading image at index ${index}`, e);
          }}
        />
      )}
    </>
  );
};

export default transition(Project);
